import {
  CHECKOUT_SESSIONS_PATH,
  CHECKOUT_UNAVAILABLE_ERROR_CODE,
  CheckoutSessionRequestSchema,
  CheckoutSessionResponseSchema,
  type CartLine,
  type CheckoutAttribution,
  type CheckoutSessionRequest,
  type CheckoutSessionResponse,
} from '@mist/contracts/commerce'
import { ApiError, postApi } from '@/lib/api-client'
import { trackApiError, trackEvent } from '@/lib/analytics'
import { captureAttribution } from '@/lib/attribution'
import { readSubscriberHandoff } from '@/lib/session'
import type { CartItem } from '@/lib/cart'

/**
 * Starts a hosted Stripe Checkout session for the bag (R-COMMERCE-02,
 * R-ARCH-06, Section 8.4).
 *
 * The request carries only what the buyer chose — kind, month/line or edition,
 * engraving, signature, quantity — never a title or a price; the API prices
 * every line from `@mist/content`. Pseudonymous subscriber/campaign IDs ride
 * along when present so the signed webhook can attribute the order; buyers
 * without them check out as unattributed. The API answers with the hosted
 * Checkout URL and the browser navigates there top-level (no embedded payment
 * script, so no CSP change), returning to `/checkout/success` or
 * `/checkout/cancelled`.
 *
 * Failures map to a small typed result so the drawer can distinguish "not open
 * yet" from a retryable fault: HTTP 503 (`CHECKOUT_UNAVAILABLE` before Stripe is
 * configured, `LAUNCH_DISABLED` before launch) reads as `unavailable`; HTTP
 * 400/422 (`VALIDATION_FAILED`, e.g. an edition no longer sold) as `invalid`;
 * everything else, including the API's 502 `CHECKOUT_PROVIDER_ERROR`, as
 * `network`. Analytics receives the attribution posture only — never engraving
 * or signature text, and never a price (amounts are reported by the webhook).
 */
export type CheckoutFailureReason = 'unavailable' | 'invalid' | 'network'

export type CheckoutResult =
  | { readonly ok: true; readonly url: string; readonly sessionId: string }
  | { readonly ok: false; readonly reason: CheckoutFailureReason; readonly message: string }

export type StartCheckoutOptions = {
  /** Navigation seam; defaults to `window.location.assign`. */
  readonly navigate?: (url: string) => void
}

export const CHECKOUT_MESSAGES: Record<CheckoutFailureReason, string> = {
  unavailable: 'Checkout is not open yet.',
  invalid: 'Something in your bag could not be checked out. Please review it and try again.',
  network: 'Checkout could not open. Please try again.',
}

/** Strips a bag row down to the contract line — no id, title, details or price. */
export function toCartLine(item: CartItem): CartLine {
  const line: CartLine = { kind: item.kind, quantity: item.quantity }
  if (item.month) line.month = item.month
  if (item.line) line.line = item.line
  if (item.editionId) line.editionId = item.editionId
  if (item.engraving) line.engraving = item.engraving
  if (item.signature) line.signature = item.signature
  return line
}

export function buildCheckoutSessionRequest(items: readonly CartItem[], attribution?: CheckoutAttribution): CheckoutSessionRequest {
  const request: CheckoutSessionRequest = { lines: items.map(toCartLine) }
  const cleaned = cleanAttribution(attribution)
  if (cleaned) request.attribution = cleaned
  return request
}

/**
 * Pseudonymous attribution for the session, mirroring the Shopify path: the
 * verified-subscriber public id from the verify handoff and the referral
 * campaign id from the landing URL. Never email, never a token.
 */
export function collectCheckoutAttribution(): CheckoutAttribution {
  const attribution: CheckoutAttribution = {}
  const handoff = readSubscriberHandoff()
  if (handoff) attribution.subscriber_pseudonymous_id = handoff.subscriberPublicId
  const campaign = captureAttribution().referralCampaignId
  if (campaign) attribution.campaign_pseudonymous_id = campaign
  return attribution
}

function cleanAttribution(attribution?: CheckoutAttribution): CheckoutAttribution | undefined {
  if (!attribution) return undefined
  const cleaned: CheckoutAttribution = {}
  if (attribution.subscriber_pseudonymous_id) cleaned.subscriber_pseudonymous_id = attribution.subscriber_pseudonymous_id
  if (attribution.campaign_pseudonymous_id) cleaned.campaign_pseudonymous_id = attribution.campaign_pseudonymous_id
  return Object.keys(cleaned).length > 0 ? cleaned : undefined
}

function failure(reason: CheckoutFailureReason): CheckoutResult {
  return { ok: false, reason, message: CHECKOUT_MESSAGES[reason] }
}

export function classifyCheckoutError(error: unknown): CheckoutFailureReason {
  if (error instanceof ApiError) {
    if (error.code === CHECKOUT_UNAVAILABLE_ERROR_CODE || error.code === 'LAUNCH_DISABLED' || error.status === 503) return 'unavailable'
    if (error.status === 400 || error.status === 422) return 'invalid'
    return 'network'
  }
  return 'network'
}

export async function startCheckout(
  items: readonly CartItem[],
  attribution?: CheckoutAttribution,
  options: StartCheckoutOptions = {},
): Promise<CheckoutResult> {
  const request = CheckoutSessionRequestSchema.safeParse(buildCheckoutSessionRequest(items, attribution))
  if (!request.success) return failure('invalid')

  const route = typeof window === 'undefined' ? '/' : window.location.pathname
  let session: CheckoutSessionResponse
  try {
    const csrfToken = readSubscriberHandoff()?.csrfToken
    session = await postApi(CHECKOUT_SESSIONS_PATH, CheckoutSessionResponseSchema, request.data, csrfToken ? { csrfToken } : {})
  } catch (error) {
    if (error instanceof ApiError && error.code !== 'NETWORK') trackApiError(CHECKOUT_SESSIONS_PATH, error)
    return failure(classifyCheckoutError(error))
  }

  const unattributed = request.data.attribution === undefined
  trackEvent('mist_checkout_started', {
    route,
    checkout_status: 'started',
    unattributed,
    ...(request.data.attribution?.campaign_pseudonymous_id
      ? { referral_campaign_id: request.data.attribution.campaign_pseudonymous_id }
      : {}),
  })

  const navigate = options.navigate ?? ((url: string) => window.location.assign(url))
  navigate(session.url)
  return { ok: true, url: session.url, sessionId: session.sessionId }
}
